// Pure presentation logic for OEM Partner screens (platform administration and the read-only OEM
// area). Nothing here is a security decision: the server authorizes every request.
import type { ManagedAccountState, ManagedAccountStatus, OEMProfile } from '../../services/oemAdmin';
import { OEM_PROFILE_FIELDS } from '../../services/oemAdmin';
import { ApiError, type Tenant } from '../../services/tenancy';

export const DIRECT_LABEL = 'Direct / RadioOS';

export interface OEMRef {
  id: string;
  name: string;
  status?: 'active' | 'disabled';
}

/** The OEM column of the platform customer list. */
export function customerOEMLabel(t: Pick<Tenant, 'oem_partner'>): string {
  if (!t.oem_partner) return DIRECT_LABEL;
  return t.oem_partner.status === 'disabled' ? `${t.oem_partner.name} (disabled)` : t.oem_partner.name;
}

/** 'all', 'direct', or an OEM Partner id. */
export type OEMFilter = string;

export function filterCustomersByOEM<T extends Pick<Tenant, 'oem_partner'>>(tenants: T[], filter: OEMFilter): T[] {
  if (filter === 'all' || !filter) return tenants;
  if (filter === 'direct') return tenants.filter((t) => !t.oem_partner);
  return tenants.filter((t) => t.oem_partner?.id === filter);
}

export const STATUS_LABEL: Record<'active' | 'disabled', string> = { active: 'ACTIVE', disabled: 'DISABLED' };

export const OEM_CHANGE_EXPLANATION =
  'This changes delegated OEM administration only. Networks, radios, groups and operational data remain with the Customer.';

export interface OEMChange {
  kind: 'attach' | 'transfer' | 'detach' | 'none';
  title: string;
  customer: string;
  current: string;
  next: string;
  explanation: string;
  confirm: string;
}

/** The confirmation of a customer's OEM change (attach, transfer or detach). */
export function describeOEMChange(customerName: string, current: OEMRef | null, next: OEMRef | null): OEMChange {
  const currentLabel = current ? current.name : DIRECT_LABEL;
  const nextLabel = next ? next.name : DIRECT_LABEL;
  let kind: OEMChange['kind'] = 'transfer';
  if ((current?.id || '') === (next?.id || '')) kind = 'none';
  else if (!current) kind = 'attach';
  else if (!next) kind = 'detach';
  const title = {
    attach: 'Assign customer to OEM Partner',
    transfer: 'Transfer customer to another OEM Partner',
    detach: 'Detach customer to Direct / RadioOS',
    none: 'No change',
  }[kind];
  const confirm = { attach: 'Assign', transfer: 'Transfer', detach: 'Detach', none: 'Close' }[kind];
  return { kind, title, customer: customerName, current: currentLabel, next: nextLabel, explanation: OEM_CHANGE_EXPLANATION, confirm };
}

/** What disabling / enabling an OEM Partner does (and does not do). */
export function describeOEMStatusChange(oemName: string, enable: boolean): { title: string; points: string[]; confirm: string } {
  if (enable) {
    return {
      title: `Enable OEM Partner "${oemName}"`,
      points: [
        'OEM administrators with a current OEM administrator role can sign in again and regain read-only OEM administration.',
        'The OEM Partner can again receive customers and administrators.',
        'Customers and their operation are not affected.',
      ],
      confirm: 'Enable',
    };
  }
  return {
    title: `Disable OEM Partner "${oemName}"`,
    points: [
      'OEM administrators lose access immediately; their signed-in sessions end.',
      'Customers remain active, and customer users remain active.',
      'Radios, PTT, SOS and GPS are not disabled.',
      'No customers, users or administrators can be added to a disabled OEM Partner.',
    ],
    confirm: 'Disable',
  };
}

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** Obvious client-side checks only; the server's validation is authoritative. */
export function validateOEMProfile(profile: OEMProfile, create: boolean): Partial<Record<keyof OEMProfile, string>> {
  const errors: Partial<Record<keyof OEMProfile, string>> = {};
  for (const f of OEM_PROFILE_FIELDS) {
    const v = (profile[f.key] ?? '').trim();
    if (f.required && (create || profile[f.key] !== undefined) && v === '') {
      errors[f.key] = `${f.label} is required.`;
      continue;
    }
    if (v.length > f.max) errors[f.key] = `${f.label} is too long (at most ${f.max} characters).`;
    else if (v && f.kind === 'email' && !EMAIL.test(v)) errors[f.key] = `${f.label} must be an e-mail address.`;
    else if (v && f.kind === 'url' && !/^https:\/\/[^\s/]+/i.test(v)) errors[f.key] = `${f.label} must start with https://`;
  }
  return errors;
}

/** Only the changed fields of an edit ("" clears an optional field). */
export function changedProfile(original: OEMProfile, edited: OEMProfile): OEMProfile {
  const out: OEMProfile = {};
  for (const f of OEM_PROFILE_FIELDS) {
    const a = (original[f.key] ?? '').trim();
    const b = (edited[f.key] ?? '').trim();
    if (a !== b) out[f.key] = b;
  }
  return out;
}

// ---- The read-only OEM area -------------------------------------------------------------------

/** The OEM area's only navigation. */
export const OEM_NAV = [{ label: 'OEM overview', path: '/OEM' }] as const;

export type OEMRoute = { kind: 'home' } | { kind: 'customer'; id: string; tab: OEMCustomerTab } | { kind: 'account' } | { kind: 'not-available' };
export type OEMCustomerTab = 'networks' | 'departments' | 'users';
export const OEM_CUSTOMER_TABS: { id: OEMCustomerTab; label: string }[] = [
  { id: 'networks', label: 'Networks' },
  { id: 'departments', label: 'Departments' },
  { id: 'users', label: 'Users' },
];

/** Which OEM screen a path shows; every other path is "not available" in the OEM area. */
export function resolveOEMRoute(pathname: string): OEMRoute {
  const p = pathname.replace(/\/+$/, '') || '/';
  if (p === '/' || p.toLowerCase() === '/oem') return { kind: 'home' };
  if (p.toLowerCase() === '/account') return { kind: 'account' }; // own account security (managed sign-in)
  const m = /^\/OEM\/customers\/([^/]+)(?:\/(networks|departments|users))?$/i.exec(p);
  if (m) return { kind: 'customer', id: decodeURIComponent(m[1]), tab: (m[2]?.toLowerCase() as OEMCustomerTab) || 'networks' };
  return { kind: 'not-available' };
}

/** Department name by id, for showing a department's parent. */
export function departmentNames(departments: { id: string; name: string }[]): Record<string, string> {
  return Object.fromEntries(departments.map((d) => [d.id, d.name]));
}

export function formatDate(iso: string): string {
  if (!iso) return '';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toISOString().slice(0, 10);
}

/** A user-facing message for a failed platform OEM request: the server's validation message, never internals. */
export function errorText(err: unknown): string {
  if (err instanceof ApiError) return err.message;
  return 'The request could not be completed. Please try again.';
}

export const IDENTITY_NOTE =
  'People are invited by e-mail and choose how they sign in: Microsoft, Google, or a RadioOS password set through a one-time activation link - you never set or see the password. ' +
  'OEM administrators always need multi-factor authentication (an authenticator app with a RadioOS password, or MFA at Microsoft/Google). ' +
  'Removing a person from the OEM only removes their OEM access.';

/** Administrator-facing labels of the derived managed-account state. */
export const ACCOUNT_STATE_LABEL: Record<ManagedAccountState, string> = {
  not_provisioned: 'No sign-in account',
  activation_pending: 'Activation pending',
  invited: 'Activation link needed',
  mfa_required: 'Password set · MFA setup required',
  active: 'Active',
  locked: 'Temporarily locked',
  disabled: 'Disabled',
};

/** Which activation action an account offers: issue, reissue (invalidates the old link) or none. */
export function activationAction(a: ManagedAccountStatus): 'issue' | 'reissue' | null {
  if (!a.provisioned || a.password_set || a.disabled) return null;
  return a.activation.pending ? 'reissue' : 'issue';
}

/** Client-side shape check of a login e-mail (ASCII user@domain); the server normalizes and decides. */
export function loginEmailError(v: string): string | undefined {
  const s = v.trim();
  if (!s) return 'Login e-mail is required.';
  if (/[^!-~]/.test(s) || !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(s)) return 'Use an ASCII e-mail address such as name@example.com.';
  return undefined;
}

/** Which recovery actions an account offers (the server decides; this only hides useless buttons). */
export function recoveryActions(a: ManagedAccountStatus): { passwordReset: boolean; mfaReset: boolean } {
  return {
    passwordReset: a.provisioned && a.password_set && !a.disabled,
    mfaReset: a.provisioned && (a.mfa.enrolled || a.password_set),
  };
}

/** The consequences an MFA reset confirmation must state. */
export function describeMFAReset(username: string, loginEmail: string): { title: string; points: string[]; confirm: string } {
  return {
    title: `Reset multi-factor authentication for ${username}`,
    points: [
      `User: ${username} (${loginEmail || 'no login e-mail'}).`,
      'The registered authenticator app is removed.',
      'All recovery codes stop working.',
      'All active sessions of this user end immediately.',
      'At the next sign-in the user must set up an authenticator app again.',
      'Confirm the user\'s identity through a trusted channel first.',
    ],
    confirm: 'Reset MFA',
  };
}
